import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'

// hack fix for resetting textarea on submit
// https://github.com/edmundhung/conform/pull/729

type BaseTextareaProps = Omit<
	React.TextareaHTMLAttributes<HTMLTextAreaElement>,
	'key'
>

interface TextareaPropsWithoutKey extends BaseTextareaProps {
	key?: string
}

export interface TextareaProps
	extends TextareaPropsWithoutKey,
		VariantProps<typeof textareaVariants> {
	rows?: number
}

const textareaVariants = cva(
	'flex w-full rounded-md border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid',
	{
		variants: {
			size: {
				default: 'min-h-[80px]',
				sm: 'min-h-[40px]',
				lg: 'min-h-[120px]',
			},
			maxSize: {
				default: 'max-h-[200px]',
				sm: 'max-h-[100px]',
				lg: 'max-h-[300px]',
			},
			resize: {
				none: 'resize-none',
				both: 'resize',
				horizontal: 'resize-x',
				vertical: 'resize-y',
			},
			textSize: {
				default: 'text-base',
				sm: 'text-sm',
				lg: 'text-lg',
			},
		},
		defaultVariants: {
			size: 'default',
			maxSize: 'default',
			resize: 'both',
			textSize: 'default',
		},
	},
)

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ className, size, maxSize, resize, textSize, ...props }, ref) => {
		return (
			<textarea
				className={cn(
					textareaVariants({ size, maxSize, resize, textSize, className }),
				)}
				ref={ref}
				{...props}
			/>
		)
	},
)
Textarea.displayName = 'Textarea'

export { Textarea, textareaVariants }
